import { isEpoqRecosPlpEnabled, isUserInTestGroupEpoqRecosPlp, epoqRecosPlpChunkSize } from "@ocm/services/ocm-object";
import pushDataLayer from "@ocm/services/dataLayer.js";

const _isProduction = location.hostname === "www.outletcity.com";
const EPOQ_PLP_AB_TEST_STORE = "productList";
const GA_DIMENSIONS = {
	variant: _isProduction ? "dimension31" : "dimension13",
	article: _isProduction ? "dimension32" : "dimension5",
	articleFiltered: _isProduction ? "dimension37" : "dimension6",
	testGroup: _isProduction ? "dimension42" : "dimension15",
};

const _getArticleDimensionAndSuffix = (isFiltered) => ({
	dimension: isFiltered ? GA_DIMENSIONS.articleFiltered : GA_DIMENSIONS.article,
	suffix: isFiltered ? "Filtered" : "",
});

const _addEpoqTestGroupDimension = (trackingData) => {
	const epoqTestGroup = window.flowConfig?.partName?.slice(-1) ?? null; // z.B. "abtest_123_B"
	if (epoqTestGroup !== null) trackingData[GA_DIMENSIONS.testGroup] = `testGroup${epoqTestGroup}`;
};

const _getStoreFromLocalStorage = () => {
	let store = [];
	try {
		store = JSON.parse(localStorage.getItem(EPOQ_PLP_AB_TEST_STORE) ?? "[]");
	} catch (error) {
		console.error(error);
	}
	return store;
};

const _writeToLocalStorage = (store) => {
	try {
		const data = JSON.stringify(store);
		localStorage.setItem(EPOQ_PLP_AB_TEST_STORE, data);
		console.log("%c%s", "color:#000;background-color:#f3d661", "update localStorage", store);
	} catch (error) {
		console.error(error);
	}
};

const _getItemFromStore = (id) => {
	const store = _getStoreFromLocalStorage();
	return store.find((item) => item.id === id) ?? null;
};

const _saveItemInStore = (data, id) => {
	const store = _getStoreFromLocalStorage();
	const itemIndex = !!id ? store.findIndex((item) => item.id === id) : -1;
	// add
	if (itemIndex === -1) {
		store.push(data);
	}
	// update
	else {
		store[itemIndex] = data;
	}

	_writeToLocalStorage(store);
};

const _removeItemsFromStore = (values) => {
	const store = _getStoreFromLocalStorage();
	const newStore = [];
	store.forEach((item) => {
		if (!values.includes(item.id)) newStore.push(item);
	});

	_writeToLocalStorage(newStore);
};

const isValidProductList = (firstProduct) => {
	const isValid = isEpoqRecosPlpEnabled && (!isUserInTestGroupEpoqRecosPlp || !!firstProduct?.epoqFeedbackUrl);
	if (isEpoqRecosPlpEnabled && !isValid)
		console.log("%c%s", "color:#fff;background-color:#35495e;", "epoq PLP is not working properly");
	return isValid;
};

const isValidProduct = (isABTestArticle, isEpoqArticle) => {
	if (!isEpoqRecosPlpEnabled || !isABTestArticle) return false;

	const isValidEpoqProduct = isUserInTestGroupEpoqRecosPlp && isEpoqArticle;
	const isValidOCMProduct = !isUserInTestGroupEpoqRecosPlp && !isEpoqArticle;

	return isValidEpoqProduct || isValidOCMProduct;
};

const isTrackedProduct = (productId) => !!_getItemFromStore(productId);

const _isNewOrSalePage = (pageData) =>
	pageData.pageType === "TopicPage" && (pageData.pagePath.includes("/neu/") || pageData.pagePath.includes("/sale/"));

const isRelevantProductList = (pageData) =>
	["CategoryPage", "BrandPage"].includes(pageData.pageType) || _isNewOrSalePage(pageData);

const hasRelevantFiltersOrNone = (query) => {
	if (!query) return true;
	if (!query.startsWith(":relevance:")) return false;

	const queryAsArray = query.slice(":relevance:".length).split(":");
	const queryKeys = new Set();
	for (let i = 0; i < queryAsArray.length; i += 2) {
		queryKeys.add(queryAsArray[i]);
	}

	return Array.from(queryKeys).every((item) => ["brand", "size", "colors"].includes(item));
};

const pushPageView = () => {
	const eventData = {
		event: "PLS_perso_variant",
		[GA_DIMENSIONS.variant]: isUserInTestGroupEpoqRecosPlp ? "epoq" : "OCM",
	};
	_addEpoqTestGroupDimension(eventData);
	console.log("%c%s", "color:#fff;background-color:deeppink", "Product List View", eventData);
	pushDataLayer(eventData);
};

const _getTrackingDataNewItem = ({ id, isABTestArticle, isEpoqArticle, isFilteredList }) => {
	if (!isABTestArticle) {
		return {};
	}

	const newItem = {
		id,
		filteredList: isFilteredList,
		lastClick: isEpoqArticle ? "epoq" : "OCM",
	};
	_saveItemInStore(newItem);

	const { dimension, suffix } = _getArticleDimensionAndSuffix(newItem.filteredList);
	console.log(
		"%c%s",
		"color:#fff;background-color:deepskyblue",
		`new item. 32: ${newItem.lastClick}${suffix}, filtered list: ${newItem.filteredList}`
	);
	return { [dimension]: `${newItem.lastClick}${suffix}` };
};

const _getTrackingDataExistingItem = ({ id, isABTestArticle, isEpoqArticle, isFilteredList }, item) => {
	if (item.filteredList !== isFilteredList) {
		item.filteredList = isFilteredList;
		_saveItemInStore(item, id);
	}

	const { dimension, suffix } = _getArticleDimensionAndSuffix(item.filteredList);
	const currentClick = isABTestArticle ? (isEpoqArticle ? "epoq" : "OCM") : "";

	if (item.lastClick !== currentClick) {
		item.lastClick = currentClick;
		_saveItemInStore(item, id);
	}

	if (isABTestArticle)
		console.log(
			"%c%s",
			"color:#fff;background-color:deepskyblue",
			`32: ${item.lastClick}${suffix}, filtered list: ${item.filteredList}`
		);
	return isABTestArticle ? { [dimension]: `${item.lastClick}${suffix}` } : {};
};

const setItemGetTrackingData = (aBTestData) => {
	const item = _getItemFromStore(aBTestData.id);
	const trackingData =
		item === null ? _getTrackingDataNewItem(aBTestData) : _getTrackingDataExistingItem(aBTestData, item);
	_addEpoqTestGroupDimension(trackingData);
	return trackingData;
};

const getTrackingData = (productId) => {
	const item = _getItemFromStore(productId);

	if (item === null || !item.lastClick) return {};
	const { dimension, suffix } = _getArticleDimensionAndSuffix(item.filteredList);
	const trackingData = { [dimension]: `${item.lastClick}${suffix}` };
	_addEpoqTestGroupDimension(trackingData);
	return trackingData;
};

const getEECTrackingData = (aBTestData) => {
	const trackingData = {};
	if (hasRelevantFiltersOrNone(aBTestData.query) && aBTestData.position < epoqRecosPlpChunkSize) {
		const { dimension, suffix } = _getArticleDimensionAndSuffix(!!aBTestData.query);
		trackingData[dimension] = `${aBTestData.isEpoqArticle ? "epoq" : "OCM"}${suffix}`;
		_addEpoqTestGroupDimension(trackingData);
	}

	return trackingData;
};

const removeTrackingData = (productIds) => {
	_removeItemsFromStore(productIds);
};

const TrackingService = {
	isValidProductList,
	isValidProduct,
	isTrackedProduct,
	isRelevantProductList,
	hasRelevantFiltersOrNone,
	pushPageView,
	setItemGetTrackingData,
	getTrackingData,
	getEECTrackingData,
	removeTrackingData,
};
export default TrackingService;
